import { useCallback, useState, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { s, MdmShell, Field, ErrorAlert } from './_mdmKit.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/** M05 · C2 — Master Data Export Console.
 * Wires the frozen `/master/export` contract:
 *   POST `/master/export` {entityType,exportFormat,recordCount,filterParams?,storagePath?,isSensitiveExport?} → 201 (T072)
 *   GET  `/master/export/:exportId/download` → {storagePath,exportId}
 * The backend has no list endpoint, so exports requested in this session are
 * accumulated client-side into the "Recent exports" table. */

const ENTITY_TYPES = [
  'CUSTOMER', 'VENDOR', 'PAINTER', 'WORK_TYPE', 'REJECTION_REASON',
  'TAT', 'RATE_CATEGORY', 'LOOKUP_CATEGORY', 'LOOKUP_VALUE', 'NOTIFICATION_TEMPLATE',
] as const;
const EXPORT_FORMATS = ['XLSX', 'CSV', 'PDF', 'JSON'] as const;

interface ExportLog {
  exportId: string;
  entityType: string;
  exportFormat: string;
  recordCount: number;
  filterParams: Record<string, unknown> | null;
  storagePath: string | null;
  isSensitiveExport: boolean;
  exportedAt: string;
  downloadExpiresAt: string | null;
}

function errText(e: unknown): string {
  if (e && typeof e === 'object') {
    const body = (e as { body?: unknown }).body;
    if (body && typeof body === 'object' && 'message' in body) {
      const m = (body as { message?: unknown }).message;
      if (typeof m === 'string') return m;
      if (Array.isArray(m)) return m.join(', ');
    }
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return 'Request failed. Please try again.';
}

export function MasterExportConsolePage() {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [exports, setExports] = useState<ExportLog[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [downloads, setDownloads] = useState<Record<string, string>>({});

  const [entityType, setEntityType] = useState<string>('');
  const [exportFormat, setExportFormat] = useState<string>('XLSX');
  const [recordCount, setRecordCount] = useState<string>('');
  const [storagePath, setStoragePath] = useState<string>('');
  const [filterJson, setFilterJson] = useState<string>('');
  const [isSensitive, setIsSensitive] = useState<boolean>(false);

  const submit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      if (!entityType) {
        setError(t('mdm.export.selectEntity'));
        return;
      }
      const count = Number(recordCount);
      if (!Number.isInteger(count) || count < 1) {
        setError(t('mdm.export.recordInvalid'));
        return;
      }
      let filterParams: Record<string, unknown> | undefined;
      if (filterJson.trim()) {
        try {
          const parsed = JSON.parse(filterJson);
          if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            throw new Error('not an object');
          }
          filterParams = parsed as Record<string, unknown>;
        } catch {
          setError(t('mdm.export.filterInvalid'));
          return;
        }
      }
      setSaving(true);
      try {
        const created = await apiRequest<ExportLog>('/master/export', {
          method: 'POST',
          token,
          body: {
            entityType,
            exportFormat,
            recordCount: count,
            ...(filterParams ? { filterParams } : {}),
            ...(storagePath.trim() ? { storagePath: storagePath.trim() } : {}),
            isSensitiveExport: isSensitive,
          },
        });
        setExports((prev) => [created, ...prev]);
        setRecordCount('');
        setStoragePath('');
        setFilterJson('');
        setIsSensitive(false);
      } catch (err) {
        setError(errText(err));
      } finally {
        setSaving(false);
      }
    },
    [entityType, exportFormat, recordCount, storagePath, filterJson, isSensitive, token],
  );

  const download = useCallback(
    async (exportId: string) => {
      setError(null);
      try {
        const res = await apiRequest<{ storagePath: string; exportId: string }>(
          `/master/export/${exportId}/download`,
          { token },
        );
        setDownloads((prev) => ({ ...prev, [exportId]: res.storagePath }));
      } catch (err) {
        setError(errText(err));
      }
    },
    [token],
  );

  return (
    <MdmShell
      label="Master data export console"
      title={t('mdm.export.title')}
      description={t('mdm.export.description')}
      backTo="/admin/mdm/lookups"
      backLabel={t('mdm.common.backLabel')}
    >
      {error && <ErrorAlert message={error} />}

      <form aria-label="Create master data export" onSubmit={submit} style={{ ...s.card, marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
          <Field id="exp-entity" label={t('mdm.export.entityType')}>
            <select id="exp-entity" style={s.input} value={entityType} onChange={(e) => setEntityType(e.target.value)}>
              <option value="">{t('mdm.export.selectPh')}</option>
              {ENTITY_TYPES.map((et) => (
                <option key={et} value={et}>{et}</option>
              ))}
            </select>
          </Field>
          <Field id="exp-format" label={t('mdm.export.exportFormat')}>
            <select id="exp-format" style={s.input} value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}>
              {EXPORT_FORMATS.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </Field>
          <Field id="exp-count" label={t('mdm.export.recordCount')} hint={t('mdm.export.recordHint')}>
            <input id="exp-count" style={s.input} type="number" value={recordCount} onChange={(e) => setRecordCount(e.target.value)} />
          </Field>
          <Field id="exp-storage" label={t('mdm.export.storagePath')} hint={t('mdm.export.storageHint')}>
            <input id="exp-storage" style={s.input} value={storagePath} onChange={(e) => setStoragePath(e.target.value)} />
          </Field>
        </div>
        <div style={{ marginTop: 14 }}>
          <Field id="exp-filter" label={t('mdm.export.filterParams')} hint={t('mdm.export.filterHint')}>
            <textarea id="exp-filter" style={{ ...s.input, minHeight: 64, fontFamily: 'monospace' }} value={filterJson} onChange={(e) => setFilterJson(e.target.value)} />
          </Field>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <input type="checkbox" checked={isSensitive} onChange={(e) => setIsSensitive(e.target.checked)} />
          <span>{t('mdm.export.sensitive')}</span>
        </label>
        <div style={{ marginTop: 16 }}>
          <Button type="submit" data-testid="create-export" variant="primary" disabled={saving}>
            {saving ? t('mdm.export.requesting') : t('mdm.export.requestExport')}
          </Button>
        </div>
      </form>

      <h2 style={{ fontSize: 16, margin: '0 0 10px' }}>{t('mdm.export.recentTitle')}</h2>
      {exports.length === 0 ? (
        <p data-testid="exports-empty" style={{ color: s.muted }}>{t('mdm.export.empty')}</p>
      ) : (
        <DataTable<ExportLog>
          ariaLabel="Recent exports"
          testId="exports-table"
          rows={exports}
          rowKey={(x) => x.exportId}
          rowTestId={(x) => `export-row-${x.exportId}`}
          columns={[
            { key: 'entity', header: t('mdm.export.colEntity'), render: (x) => x.entityType },
            { key: 'format', header: t('mdm.export.colFormat'), render: (x) => x.exportFormat },
            { key: 'records', header: t('mdm.export.colRecords'), render: (x) => x.recordCount },
            { key: 'sensitive', header: t('mdm.export.colSensitive'), render: (x) => (x.isSensitiveExport ? t('mdm.common.yes') : t('mdm.common.no')) },
            { key: 'requested', header: t('mdm.export.colRequested'), render: (x) => (x.exportedAt ? at(x.exportedAt) : '—') },
            {
              key: 'download',
              header: t('mdm.export.colDownload'),
              render: (x) => (
                downloads[x.exportId] ? (
                  <a data-testid={`download-link-${x.exportId}`} href={downloads[x.exportId]}>
                    {downloads[x.exportId]}
                  </a>
                ) : (
                  <Button type="button" data-testid={`download-${x.exportId}`} variant="secondary" onClick={() => download(x.exportId)}>
                    {t('mdm.export.getLink')}
                  </Button>
                )
              ),
            },
          ]}
        />
      )}
    </MdmShell>
  );
}

export default MasterExportConsolePage;
